/**
 * English translation strings
 * Used throughout the application for UI text, pages, and messages
 *
 * This is the framework template with common, auth, wallet, and navigation strings.
 * Business-specific translation keys should be added per feature module requirements.
 */
export const en = {
  welcome: {
    title: "Your money.\nYour move.",
    description: "Chat, pay and trade. You decide. Your Agent helps.",
    create: "Create wallet",
    existing: "I already have a wallet",
    security: "Your keys stay yours. You decide what your Agent can do.",
    language: "Change language",
    restoring: "Opening your space…",
    authenticating: "Review the sign-in request to continue.",
    name: "Wallet name",
    optional: "Optional",
    namePlaceholder: "My wallet",
    setupTitle: "Make it yours.",
    setupDescription: "Create a wallet on this device. Keep the recovery phrase somewhere safe.",
    deviceTitle: "Protected by your device",
    deviceDescription: "Your device will ask you to verify twice: first to create a credential, then to encrypt your wallet.",
    backupTitle: "A recovery phrase to keep",
    backupDescription: "Next, save your recovery words and check your copy.",
    createWithDevice: "Create with device verification",
    checking: "Checking this device…",
    unsupported: "This browser cannot use device verification. You can use another available wallet option.",
    limit: "This device has reached its local-wallet limit. Choose an existing wallet.",
    checkFailed: "Device support could not be checked. Retry or choose another wallet option.",
    retry: "Try again",
    stepCredential: "Create a credential",
    stepEncryption: "Encrypt your wallet",
    credentialDetail: "Approve the request shown by your device.",
    encryptionDetail: "Verify once more to encrypt your wallet keys.",
    actualProgress: "Follow your device’s prompts. This screen updates as each step changes.",
    recoveryWarning: "Keep these words private and in order. Anyone with them can control your wallet.",
    recoveryCopy: "Copy words",
    recoveryCopied: "Copied",
    recoveryContinue: "Check my copy",
    verifyTitle: "Check your recovery copy.",
    verifyDescription: "Enter the requested words to check that you saved them correctly.",
    word: "Word {{number}}",
    verifyComplete: "Finish checking",
    copiedFailure: "Could not copy. Write the words down before continuing.",
    createFailed: "Wallet creation did not finish. Your existing wallets have not been removed.",
    previewNote: "Design preview. No wallet, credential or recovery phrase is created.",
  },
  // Brand identity
  brand: {
    name: "DeTrust",
    tagline: "Your personal on-chain AI agent",
    logoLabel: "App Logo",
  },

  // Common UI elements
  common: {
    welcome: "Welcome",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    confirm: "Confirm",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    back: "Back",
    next: "Next",
    previous: "Previous",
    submit: "Submit",
    search: "Search",
    filter: "Filter",
    clear: "Clear",
    all: "All",
    none: "None",
    yes: "Yes",
    no: "No",
    optional: "Optional",
    required: "Required",
    comingSoon: "Coming Soon",
    popular: "Popular",
    noData: "No data available",
    copyAddress: "Copy Address",
    copied: "Copied!",
    disconnect: "Disconnect",
    connect: "Connect",
    viewDetails: "View Details",
    learnMore: "Learn More",
    retry: "Retry",
    of: "of",
    searchCountry: "Search country...",
    unknownError: "An unknown error occurred",
  },

  // Navigation
  nav: {
    home: "Home",
    chat: "Chat",
    dashboard: "Dashboard",
    strategies: "Strategies",
    settings: "Settings",
    profile: "Profile",
    help: "Help",
    docs: "Documentation",
  },

  // Authentication and wallet
  auth: {
    connectWallet: "Connect Wallet",
    walletConnected: "Wallet Connected",
    walletDisconnected: "Wallet Disconnected",
    selectWallet: "Select Your Wallet",
    installedWallets: "Installed Wallets",
    otherWallets: "Other Wallets",
    connectingWallet: "Connecting Wallet...",
    disconnectWallet: "Disconnect Wallet",
    wrongNetwork: "Wrong Network",
    switchNetwork: "Switch Network",
    walletRequired: "Wallet connection required",
    connectToView: "Please connect your wallet to view this page",
    noWalletDetected: "No wallet detected",
    installWallet: "Please install a Solana wallet",
    copyAddressSuccess: "Address copied to clipboard",
    // LoginButton specific
    signIn: "Sign In",
    signingIn: "Signing In...",
    connecting: "Connecting...",
    connectedWallet: "Connected Wallet",
    loggedInAs: "Logged in as {{address}}. Click to open wallet menu",
    connectWalletToSignIn: "Connect Solana wallet to sign in",
    signInWithConnectedWallet: "Sign in with connected wallet",
    logoutFromWallet: "Logout from wallet",
    logout: "Logout",
    loggingOut: "Logging out...",
    reconnectWallet: "Reconnect Wallet",
    reconnectWalletDesc:
      "Your wallet has been disconnected. Please reconnect to continue.",
    connectAndSignIn: "Connect & Sign In",
  },

  // Error messages
  errors: {
    generic: "An error occurred. Please try again.",
    networkError: "Network error. Please check your connection.",
    walletConnection: "Failed to connect wallet",
    walletDisconnection: "Failed to disconnect wallet",
    transactionRejected: "Transaction rejected by user",
    transactionFailed: "Transaction failed",
    insufficientFunds: "Insufficient funds",
    invalidAmount: "Invalid amount",
    invalidAddress: "Invalid address",
    slippageExceeded: "Price slippage exceeded tolerance",
    deadline: "Transaction deadline exceeded",
    gasEstimation: "Failed to estimate gas",
    contractError: "Smart contract error",
    notFound: "Resource not found",
    unauthorized: "Unauthorized access",
    forbidden: "Access forbidden",
    validationError: "Validation error",
    serverError: "Server error. Please try again later.",
    timeout: "Request timeout",
  },

  // Success messages
  success: {
    walletConnected: "Wallet connected successfully",
    walletDisconnected: "Wallet disconnected successfully",
    transactionSubmitted: "Transaction submitted",
    transactionConfirmed: "Transaction confirmed",
    settingsSaved: "Settings saved successfully",
    profileUpdated: "Profile updated successfully",
    copied: "Copied to clipboard",
  },

  // Footer
  footer: {
    tagline: "Building on Solana",
    product: "Product",
    features: "Features",
    documentation: "Documentation",
    company: "Company",
    about: "About Us",
    blog: "Blog",
    careers: "Careers",
    contact: "Contact",
    legal: "Legal",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    copyright: "2026 DeTrust. All rights reserved.",
  },

  // Wallet connection
  wallet: {
    connecting: "Connecting...",
    connectWallet: "Connect Wallet",
    copyAddress: "Copy Address",
    viewOnExplorer: "View on Explorer",
    disconnect: "Disconnect",
    paymentToken: "USDC",
    secondaryToken: "TOKEN",
    balance: "Balance",
    refresh: "Refresh",
    transfer: "Transfer",
    send: "Send",
    selectToken: "Select Token",
    confirmInWallet: "Please confirm in your wallet...",
    viewTransaction: "View Transaction",
    done: "Done",
    tryAgain: "Try Again",
    solReserveHint: "0.01 SOL reserved for fees",
    pasteAddress: "Paste",
    recipientAddress: "Recipient Address",
    amount: "Amount",
    maxAmount: "Max",
    sending: "Sending...",
    invalidAddress: "Invalid Solana address",
    insufficientBalance: "Insufficient balance",
    selfTransferError: "Cannot send to yourself",
    transferBalanceRefreshUnavailable: "The transfer is confirmed, but the balance display could not refresh.",
    transferAuthority: "Your wallet signs this transfer",
    transferUnknown: "The outcome is not confirmed. Check the original transaction before sending again.",
    transferRecoveryUnavailable: "Saved transaction status is unavailable. Sending is paused until it can be checked.",
    checkOriginalTransaction: "Check original transaction",
    checkingOriginalTransaction: "Checking original transaction…",
    transferFailedOnChain: "This transaction failed on chain.",
    transferNotSubmitted: "The transaction was not submitted.",
    transferSuccess: "Transfer Successful",
    available: "Available",
  },

  // Login modal
  loginModal: {
    signInToApp: "Sign In",
    securelyAuthenticate: "Securely authenticate using your Solana wallet",
    reviewSignatureRequest: "Review Signature Request",
    reviewMessageBeforeSigning: "Review the message below before signing",
    signingMessage: "Signing Message",
    completeSignatureRequest:
      "Please complete the signature request in your wallet",
    success: "Success!",
    youAreSignedIn: "You are now signed in",
    error: "Error",
    somethingWentWrong: "Something went wrong",
    secureConnection: "Secure Connection",
    connectYourWallet: "Connect Your Wallet",
    connectToSignIn:
      "Connect your Solana wallet to sign in securely. Your wallet will be used to verify your identity.",
    encrypted: "Encrypted",
    secure: "Secure",
    yourKeys: "Your Keys",
    waitingForSignature: "Waiting for Signature",
    approveInWallet: "Please approve the signature request in your wallet...",
    approveInWalletNamed:
      "Please approve the signature request in {{wallet}}...",
    secureSigningInProgress: "Secure signing in progress",
    selectWallet: "Select Your Wallet",
    selectYourWallet: "Select Your Wallet",
    chooseWalletFromList:
      "Choose from Phantom, Solflare, Ledger, or other supported wallets",
    pickFromSupportedWallets:
      "Pick from 20+ supported wallets including hardware wallets",
    preparingSignature: "Preparing signature request...",
    signingIn: "Signing In",
    preparingAuth: "Preparing authentication...",
    authenticationSuccessful: "Authentication Successful!",
    successfullyAuthenticated: "You have been successfully authenticated.",
    authenticationFailed: "Authentication Failed",
    errorOccurred: "An error occurred during authentication. Please try again.",
    cancel: "Cancel",
    connectWalletBtn: "Connect Wallet",
    signMessage: "Sign Message",
    checkWalletForSignature: "Check your wallet for the signature request...",
    close: "Close",
    tryAgain: "Try Again",
  },

  // Desktop gate
  desktopGate: {
    title: "Desktop Only",
    description:
      "This application is optimized for desktop browsers. Please use a device with a screen width of at least 1024px.",
  },

  // Chat (the conversation on vNext — spec 2026-09-06 §3.2, Task 4)
  chat: {
    signing: {
      authority: "Your wallet approves this operation",
      title: "Wallet signature request",
      connectOwner: "Connect the wallet that owns this request.",
      unavailable: "Request status unavailable",
      loading: "Reading this operation…",
      prepared: "Waiting for your signature",
      submitted: "Submitted; checking receipt",
      unknown: "Outcome unknown",
      confirmed: "Confirmed",
      finalized: "Finalized",
      failed: "Failed on chain",
      rejected: "Request rejected",
      expired: "Prepared transaction expired",
      working: "Working…",
      wallet: "Signing wallet",
      programs: "Programs",
      signature: "Original signature",
      keepOriginal: "Keep checking this operation. No replacement transaction will be built or signed automatically.",
      sign: "Review and sign",
      reject: "Reject unsigned request",
      check: "Check operation",
      retrySigned: "Retry original signed transaction",
      listUnavailable: "Signing requests could not be read. This does not mean there are no pending requests.",
    },
    sidebar: {
      title: "Conversations",
      newConversation: "New conversation",
      search: "Search conversations",
      searchPlaceholder: "Search conversations…",
      loading: "Loading conversations",
      empty: "No conversations yet. Start a new one.",
      noMatch: "No conversations match your search.",
      noMessages: "No messages yet",
      needsYou: "Needs you",
      pinned: "Pinned",
      actions: "Session actions",
      pin: "Pin",
      unpin: "Unpin",
      rename: "Rename",
      renameLabel: "New title",
      save: "Save",
      cancel: "Cancel",
      markRead: "Mark as read",
      archive: "Archive",
      groups: {
        pinned: "Pinned",
        today: "Today",
        yesterday: "Yesterday",
        thisWeek: "This week",
        older: "Older",
      },
      category: {
        trading: "Trading",
        shopping: "Shopping",
        defi: "DeFi",
        general: "General",
        notification: "Notifications",
        task: "Tasks",
      },
    },
    time: {
      justNow: "just now",
      minutes: "{{n}}m",
      hours: "{{n}}h",
      days: "{{n}}d",
      yesterday: "Yesterday",
    },
    thread: {
      title: "Agent chat",
      you: "You",
      agent: "Agent",
      clear: "Clear",
      emptyTitle: "Start a conversation with your personal agent.",
      emptyHint: "Ask about prices, portfolio, risk analysis, or trading strategies.",
      connecting: "Connecting…",
      offline: "Offline — reconnecting",
      connectionError: "Connection error",
      using: "Using {{skill}}…",
    },
    composer: {
      label: "Message",
      placeholder: "Send a message…",
      thinking: "Agent is thinking…",
      send: "Send message",
      notSent: "Not sent — you are offline. Your draft is kept.",
      quick: {
        portfolio: "Portfolio",
        portfolioText: "Show my portfolio",
        goldPrice: "Gold price",
        goldPriceText: "What's the gold price?",
        strategies: "My strategies",
        strategiesText: "Show my active strategies",
        riskProfile: "Risk profile",
        riskProfileText: "Show my risk profile",
      },
    },
    bubble: {
      noResponse: "I wasn't able to generate a response. Please try again.",
    },
    context: {
      panel: "Context",
      awaiting: "Waiting for the agent's snapshot…",
      spendingRoom: "Spending room today",
      spendingRoomHint: "What the agent may still spend today under your rules. Not a balance.",
      spendingRoomUnknown: "Not reported on this connection yet",
      ledger: "Holdings ledger",
      ledgerReady: "Up to date",
      ledgerHeld: "Held — catching up with the chain",
      ledgerUnknown: "Not reported",
      decisions: "Waiting on you",
      noDecisions: "Nothing is waiting on you.",
      review: "Review",
      decisionTransaction: "Transaction needs your approval",
      decisionTransfer: "Payment to a person needs your approval",
      decisionOther: "{{type}} needs your approval",
      amountInThread: "Amount is stated in the thread",
      tier: "T{{tier}}",
      strategies: "Active strategies",
      noStrategies: "No active strategies",
      strategyActive: "Active",
      strategyPaused: "Paused",
      strategyTriggered: "Triggered",
      strategyError: "Failed",
      incidents: "Open incidents",
      noIncidents: "No open incidents",
    },
    approval: {
      approvalRequired: "Approval required",
      withinRules: "Within your rules",
      cancellable: "Done, cancellable",
      doneUnderRules: "Done under your rules",
      approvedByYou: "Approved by you",
      rejected: "Declined by you",
      expired: "Expired before you decided",
      revoked: "Approval revoked",
      simulated: "Simulated — nothing was sent",
      failed: "The transaction failed. This act is spent; there is nothing to retry.",
      awaitingReceipt: "Settled per the stream, but this card's own receipt has not been found in today's timeline yet.",
      foreignReceipt: "The event given is not this card's own settled receipt.",
      signatureDiffers: "The receipt names a different transaction than the one this card was sent as.",
      legacyConfirmed: "A record written before settlement was separated from sending; check its receipt.",
      notRecorded: "not recorded on the receipt",
    },
    card: {
      agentWallet: "Agent wallet",
      decline: "Decline",
      approve: "Approve",
      checkReceipt: "Check receipt",
      viewReceipt: "View receipt",
      viewTransaction: "View transaction",
      quoteDetails: "Quote details",
      youReceive: "You receive",
      rate: "Rate",
      priceImpact: "Price impact",
      minReceived: "Minimum received",
      gas: "Network fee",
      quoteValid: "Quote valid for {{time}}",
      quoteStale: "This quote has passed its time. Waiting for the server to close it; nothing can be approved on it.",
      nothingMoved: "Nothing moved. This is the quote as it stood.",
      warnings: "Warnings",
      contractWhitelisted: "Contract whitelisted",
      simulationPassed: "Simulation passed",
      noUnlimitedApproval: "No unlimited approval",
      evidenceChainValid: "Evidence chain valid",
      actNotSent: "Not sent — you are offline. Retry sends the same decision, once.",
      actUnknown: "No answer to your decision yet. Check its receipt before deciding again.",
      retry: "Retry",
      reported: "Reported",
      reportedHint: "Reported by the agent; its own settled receipt has not been found in today's timeline.",
      total: "Total",
      fee: "Fee",
      delivery: "Delivery",
      order: "Order",
      orderPlaced: "Order placed",
      orderProcessing: "Order processing",
      orderFailed: "Order failed",
      ifWrong: "If it goes wrong",
      irreversible: "Irreversible",
      waitingOnYou: "Waiting on you",
      doneCancellable: "Done, cancellable",
      withinRules: "Within your rules",
      blocked: "Blocked",
      byAgent: "by your agent",
    },
  },

  // Dashboard (vNext page migration, Task 5)
  dashboard: {
    title: "Dashboard",
    subtitle: "What came back today, and how the agent is doing",
    loading: "Loading dashboard...",
    loadFailed: "Failed to load dashboard data.",
    checkConnection: "Please check your connection and try again.",
    portfolio: {
      title: "Portfolio value",
      unavailable: "Amount unavailable",
      readFailed:
        "We could not read your balance just now. Nothing has changed; try again in a moment.",
      solHeld: "SOL held",
    },
    strategies: {
      title: "Active strategies",
      count: "{{count}} total",
      empty: "No strategies configured yet.",
      lastTriggered: "Last triggered",
      never: "Never",
      executions: "Executions",
      status: {
        draft: "Draft",
        active: "Active",
        paused: "Paused",
        triggered: "Triggered",
        completed: "Completed",
        failed: "Failed",
      },
    },
    freshness: {
      title: "Context freshness",
      notReported: "Not reported",
      good: "Good",
      stale: "Stale",
      critical: "Critical",
    },
    health: {
      title: "Agent health",
      skills: "Skills",
      healthy: "{{healthy}}/{{total}} healthy",
      entropy: "Entropy",
      anchorsAge: "Anchors age",
      hours: "{{hours}}h",
      notReported: "Not reported",
    },
    quickActions: {
      title: "Quick actions",
      chat: "Chat",
      newStrategy: "New strategy",
      portfolio: "Portfolio",
      settings: "Settings",
    },
    feed: {
      title: "What came back",
      count: "{{count}} events",
      empty: "Nothing has come back yet.",
      loadFailed: "Today's receipts could not be read.",
      retry: "Try again",
      refresh: "Refresh",
      recorded: "Recorded",
    },
  },

  // Agent wallet (vNext page migration, Task 5)
  agentWallet: {
    title: "Agent wallet",
    subtitle: "The wallet your agent signs with, under your rules",
    loading: "Reading the agent wallet...",
    notInitialised:
      "Unable to load the agent wallet. The agent may not be initialised yet.",
    authority: "The agent signs with this wallet under your rules",
    address: "Deposit address",
    copy: "Copy address",
    copied: "Copied",
    trustNote:
      "This wallet is held by your agent. Deposit only what you are comfortable letting it spend; the balance is the hard cap.",
    sweep: {
      title: "Sweep to your wallet",
      description:
        "Moves the agent wallet's SOL to an address you choose. It asks first, and settles only from its own receipt.",
      destinationLabel: "Destination address",
      destinationPlaceholder: "A Solana address",
      invalidDestination: "That is not a Solana address.",
      review: "Review sweep",
      reviewing: "Reading the balance",
      balanceReadFailed: "The balance could not be read. Nothing was sent.",
      walletNotReady: "The agent wallet is not ready yet.",
      needsYou: "Needs your approval",
      approved: "Approved by you",
      recipient: "To",
      account: "Agent wallet",
      reserveNote:
        "The agent wallet's balance. The server keeps a small reserve for the fee and rent; the receipt shows what moved.",
      confirm: "Confirm sweep",
      edit: "Edit",
      check: "Check receipt",
      startOver: "Start over",
      startOverNote:
        "This operation was refused before submission. Reviewing again reads the current agent wallet balance.",
      receipt: "View receipt",
      transactionFailed: "The sweep transaction failed on chain.",
      submissionUnknown: "The sweep result is unknown. Check the original operation before starting another sweep.",
      sentAs:
        "Transaction signature {{signature}}. Its own receipt (sweep.settled) has not been found yet.",
      notRecorded:
        "The server sent it but could not write it down: {{warning}}",
      foreignReceipt:
        "The settled event found is not this sweep's own receipt.",
      signatureDiffers:
        "The receipt names a different transaction than the one this sweep was sent as.",
      sendFailed: "The sweep could not be sent.",
    },
    log: {
      title: "Recent executions",
      empty: "No executions recorded yet.",
      settled: "Settled",
      reported: "Reported",
      unknown: "Result unknown",
      failed: "Failed",
    },
    triggers: {
      title: "Scheduled triggers",
      count: "{{count}} active",
      empty: "No active triggers. Create one via chat or API.",
      active: "Active",
      paused: "Paused",
      next: "Next",
      fired: "Fired {{count}}×",
      pause: "Pause",
      resume: "Resume",
      cancel: "Cancel",
    },
  },

  // ── vNext page migration, Task 6: strategies, agent settings, bind, CLI auth ──
  strategy: {
    title: "Strategies",
    subtitle: "Rules your agent runs on your behalf",
    newStrategy: "New strategy",
    createStrategy: "Create strategy",
    createTitle: "Create strategy",
    editTitle: "Edit strategy",
    createSubtitle: "Describe your strategy in natural language, or start from a template.",
    editSubtitle: "Modify your strategy's parameters and rules.",
    emptyTitle: "No strategies yet",
    emptyDetail:
      "Create your first automated strategy. Use natural language or choose from pre-built templates to get started.",
    backToStrategies: "Back to strategies",
    edit: "Edit",
    executions: "Evaluations",
    lastRun: "Last condition match",
    never: "Never",
    created: "Created",
    lastUpdated: "Last updated",
    rules: "Strategy rules",
    backtest: "Backtest",
    backtestUnavailable: "Backtesting is not available yet.",
    executionHistory: "Evaluation history",
    historyUnavailable: "Could not load evaluation history.",
    executionNote:
      "Each row is the agent's own evaluation record, not a receipt. Money that settled appears on the dashboard from its own event.",
    noExecutions: "No evaluations recorded yet.",
    colTime: "Time",
    colAction: "Action",
    colAsset: "Asset",
    colAmount: "Rule amount",
    colStatus: "Status",
    colReceipt: "Receipt",
    noReceipt: "Not on this wire",
    viewOnExplorer: "View on explorer",
    status: {
      active: "Active",
      paused: "Paused",
      draft: "Draft",
      triggered: "Triggered",
      completed: "Completed",
      failed: "Failed",
    },
    execution: {
      reported: "Reported",
      pending: "Pending",
      failed: "Failed",
    },
    template: {
      dca: "DCA",
      rsi: "RSI",
      priceTrigger: "Price trigger",
      custom: "Custom",
    },
    notFound: "Strategy not found",
    notFoundDetail: "This strategy does not exist.",
    saveStrategy: "Save strategy",
    updateStrategy: "Update strategy",
    saving: "Saving",
    convert: "Convert to strategy",
    converting: "Converting",
    amountUsd: "Amount",
    amountInvalid: "Enter a dollar amount with at most two decimals",
    relative: {
      justNow: "Just now",
      minutes: "{{count}}m ago",
      hours: "{{count}}h ago",
      days: "{{count}}d ago",
    },
  },
  agentSettings: {
    title: "Agent settings",
    subtitle: "Configure your agent's behaviour, limits and capabilities.",
    saving: "Saving your settings",
    budget: {
      title: "Budget limits",
      description: "The most your agent may spend in a day without asking you.",
      sliderLabel: "Daily budget",
      dailyLimit: "Daily limit",
      setLimit: "Set limit",
      spentToday: "Spent today",
      resetsAt: "Resets at",
      resetNotReported: "Reset time not reported",
      range: "Whole dollars from {{min}} to {{max}}",
      invalid: "Enter a whole-dollar amount from {{min}} to {{max}}",
    },
    autoApprove: {
      perTransaction: "Per transaction",
      perDay: "Per day",
      enforcedNote:
        "The agent enforces the per-transaction limit and your daily budget. The per-day auto-approve limit and the asset list are stored with your settings.",
    },
    aiModel: {
      connected: "Connected",
      invalidKey: "Invalid key",
      disabled: "Disabled",
      notConnected: "Not connected",
      addKey: "Add API key",
      edit: "Edit",
      remove: "Remove {{provider}}",
      cancel: "Cancel",
      saveKey: "Save key",
      updateKey: "Update key",
    },
    plan: {
      refresh: "Refresh plan",
      loading: "Checking your plan…",
      error: "Could not load your plan.",
      retry: "Try again",
      proOnly: "Available on the Pro plan.",
    },
    tokens: {
      newToken: "New token",
      createToken: "Create token",
      cancel: "Cancel",
      copy: "Copy",
      copied: "Copied",
      saved: "I have saved my token",
      revoke: "Revoke token",
      shownOnce:
        "This token is shown once. Store it securely — if it is lost, revoke it and create a new one.",
    },
    export: {
      export: "Export",
      exporting: "Exporting",
    },
    anchors: {
      edit: "Edit",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving",
    },
    skills: {
      browse: "Browse skills",
      working: "Working",
      health: {
        healthy: "Health: healthy",
        degraded: "Health: degraded",
        unhealthy: "Health: unhealthy",
      },
      uninstall: "Uninstall",
      uninstalling: "Uninstalling",
      cancel: "Cancel",
      healthy: "{{count}} healthy",
      degraded: "{{count}} degraded",
      unhealthy: "{{count}} unhealthy",
      install: "Install",
      installed: "Installed",
      builtIn: "Built-in",
      comingSoon: "Coming soon",
    },
  },
  bind: {
    invalidTitle: "Invalid link",
    invalidDetail:
      "This binding link is invalid or incomplete. Please request a new link from your bot.",
    connectTo: "Connect to {{platform}}",
    intro: "Link your Solana wallet to your {{platform}} account to use your agent.",
    signInPrompt: "Sign in with your wallet to link it to {{platform}}.",
    platform: "Platform",
    platformId: "ID",
    wallet: "Wallet",
    linking: "Linking…",
    bound: "Bound",
    failed: "Failed",
    takenElsewhere: "Already linked elsewhere",
    takenElsewhereDetail:
      "This account is already linked to a different wallet. Unlink it there, or sign in with the wallet that holds it.",
    unknown: "Result unknown",
    unknownDetail:
      "The server answered, but nothing on record says this wallet is linked yet.",
    checkAgain: "Check again",
    tryAgain: "Try again",
    genericError: "Something went wrong. Please try again.",
    returnTo: {
      telegram: "You can now return to Telegram and start chatting with your agent.",
      discord: "You can now return to Discord and use slash commands with your agent.",
      mobile: "You can now return to the app and start chatting.",
    },
    platformName: {
      telegram: "Telegram",
      discord: "Discord",
      mobile: "Mobile app",
    },
  },
  // Appearance: follow the device / light / dark, kept per device like the
  // language (spec 2026-09-07 candidate-b-fidelity, D18).
  theme: {
    label: "Appearance",
    followDevice: "Follow this device",
    light: "Light",
    dark: "Dark",
    change: "Change appearance",
  },
  cliAuth: {
    title: "Authorize DeTrust CLI",
    invalidTitle: "Invalid request",
    invalidDetail: "This page must be opened by",
    copyIntro:
      "Copy the session token and paste it into the terminal prompt. It expires within 2 hours.",
    copyToken: "Copy token",
    copied: "Copied ✓",
    handing: "Handing the session to your terminal…",
    authorized: "CLI authorized",
    done: "You can return to your terminal.",
    failed: "Could not reach the CLI",
    failedDetail:
      "Could not reach the CLI on this machine (Safari blocks this — use Chrome/Firefox, or the copy fallback below).",
    thenRun: "Then run",
    andPaste: "and paste it.",
  },
};
